/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { currentTemplateVar } from '../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import { PanelRegion, PanelType, ScenarioName, StepName, SubWorkLoad } from '../../../../../logger/interfaces';
import { LogCategory } from '../../../../../logger/LogCategories';
import { StatusName } from '../../../../../logger/ScenarioLogger';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { useIsAdmin } from '../../../../../utilities/TemplateManagementUtils';
import { ErrorEvent } from '../../../../../utilities/TemplateUtils';
import { ErrorPageProps } from '../../../../containers/ErrorPage';
import { TemplateModalHeaderProps } from '../../../TemplateModalHeader/TemplateModalHeader';
import { PreSetupTemplateStepProps, PreSetupTemplateSteps } from '../PreSetupTemplateSteps';
import { PreviewTemplateComponent, PreviewTemplateComponentProps } from './PreviewTemplate.component';

export const PreviewTemplateContainer: React.FC<PreSetupTemplateStepProps> = (
    props: PreSetupTemplateStepProps
): JSX.Element => {
    const { navigateTo } = props;
    const host = getAppHost();
    const logger = host.getLogger();

    const isAdmin = useIsAdmin();
    const currentTemplate = useReactiveVar(currentTemplateVar);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<ErrorPageProps>(null);
    const [retryCount, setRetryCount] = React.useState<number>(0);
    const startTime = useRef<number>(performance.now());

    useEffect((): void => {
        logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.PreviewTemplate, StatusName.NA);
    }, []);

    const onNext = async (): Promise<void> => {
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateTemplateInstance,
            StepName.PreviewTemplate,
            'click',
            'nav',
            'ActionButton',
            'nextButton',
            PanelRegion.Modal,
            PanelType.PreSetupTemplateInstance
        );

        if (isAdmin) {
            navigateTo(PreSetupTemplateSteps.SelectScope);
        } else {
            navigateTo(PreSetupTemplateSteps.SelectTeam);
        }
    };

    const onBack = (): void => {
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateTemplateInstance,
            StepName.PreviewTemplate,
            'click',
            'nav',
            'ActionButton',
            'backButton',
            PanelRegion.Modal,
            PanelType.PreSetupTemplateInstance
        );

        navigateTo(PreSetupTemplateSteps.BrowseTemplates);
    };

    const onFormReady = (): void => {
        logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.PreviewTemplate, StatusName.Success, {
            stepElapsed: performance.now() - startTime.current,
        });
        setLoading(false);
    };

    const onError = (event: ErrorEvent): void => {
        logger.logTraceError(
            LogCategory.TemplateManagement,
            `Form template preview iframe posted error with code:${event.code}, isRetriable: ${event.isRetriable}, message: ${event.message}`
        );

        logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.PreviewTemplateLoadFailed, StatusName.Fail, {
            data: {
                code: event.code,
                isRetriable: event.isRetriable,
                message: event.message,
                errorCode: event.code,
            },
        });

        setLoading(false);
        setError({
            onAction:
                event.isRetriable &&
                ((): void => {
                    setRetryCount((value: number): number => value + 1);
                    setLoading(true);
                    setError(null);
                }),
        });
    };

    const templateModalHeaderProps: TemplateModalHeaderProps = {
        templateInstance: {
            Title: currentTemplate.title,
            Description: currentTemplate.description,
            IconName: currentTemplate.iconName,
            AccentColor: currentTemplate.iconBackgroundColor,
        },
        teamName: host.getLocale().strings.preview,
    };

    const previewProps: PreviewTemplateComponentProps = {
        onFormReady,
        onError,
        formTemplate: currentTemplate.formTemplate,
        templateModalHeaderProps,
        loading,
        error,
        onNext,
        onBack,
        retryCount,
    };

    return <PreviewTemplateComponent {...previewProps} />;
};
